import { StabilityMetric, StabilityModelTimeline } from "@/http/api-types";

// Reuse the same 26-colour chart palette the diagnostics chart uses.
export const STABILITY_SERIES_COLORS = Array.from({ length: 26 }, (_, i) => `hsl(var(--chart-${i + 1}))`);

export type StabilitySeriesDefinition = {
  key: string;
  name: string;
  color: string;
};

export type StabilityChartRow = {
  date: string;
  [seriesKey: string]: number | string | null;
};

export type StabilityChartData = {
  series: StabilitySeriesDefinition[];
  rows: StabilityChartRow[];
};

function metricValue(mdape: number | null, wape: number | null, metric: StabilityMetric): number | null {
  return metric === "mdape" ? mdape : wape;
}

// toISODate normalizes an experiment date string (which may carry a time
// component) to a YYYY-MM-DD calendar date in UTC, or null when absent/invalid.
export function toISODate(value?: string): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toISOString().slice(0, 10);
}

// enumerateDays returns every ISO date from start to end inclusive. Dates are
// handled in UTC to avoid timezone-induced off-by-one shifts.
export function enumerateDays(start: string, end: string): string[] {
  const days: string[] = [];
  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || startMs > endMs) {
    return days;
  }
  for (let ms = startMs; ms <= endMs; ms += 24 * 60 * 60 * 1000) {
    days.push(new Date(ms).toISOString().slice(0, 10));
  }
  return days;
}

// buildStabilityChartData turns the per-model timelines into recharts-ready
// rows. The x domain is a continuous daily range so a missing cut-off becomes a
// null cell — paired with connectNulls={false} on the line, that renders as a
// gap rather than a bridge. When `range` is given (the experiment's window) the
// domain is clamped to it and cut-offs outside the window are dropped;
// otherwise it spans every model's cut-offs.
export function buildStabilityChartData(
  models: StabilityModelTimeline[],
  metric: StabilityMetric,
  range?: { start: string; end: string },
): StabilityChartData {
  const series: StabilitySeriesDefinition[] = models.map((model, index) => ({
    key: `m${index}`,
    name: model.name,
    color: STABILITY_SERIES_COLORS[index % STABILITY_SERIES_COLORS.length],
  }));

  const valueByDate = new Map<string, Map<string, number>>();
  let min: string | null = null;
  let max: string | null = null;

  models.forEach((model, index) => {
    const key = `m${index}`;
    for (const point of model.points) {
      const date = point.last_period_end;
      if (range && (date < range.start || date > range.end)) continue;
      if (min === null || date < min) min = date;
      if (max === null || date > max) max = date;

      const value = metricValue(point.mdape, point.wape, metric);
      if (value === null) continue;

      let row = valueByDate.get(date);
      if (!row) {
        row = new Map<string, number>();
        valueByDate.set(date, row);
      }
      row.set(key, value);
    }
  });

  const start = range ? range.start : min;
  const end = range ? range.end : max;
  if (start === null || end === null) {
    return { series, rows: [] };
  }

  const rows: StabilityChartRow[] = enumerateDays(start, end).map(date => {
    const row: StabilityChartRow = { date };
    const values = valueByDate.get(date);
    for (const def of series) {
      row[def.key] = values?.get(def.key) ?? null;
    }
    return row;
  });

  return { series, rows };
}

export function hasStabilityMetricData(rows: StabilityChartRow[], series: StabilitySeriesDefinition[]): boolean {
  return rows.some(row => series.some(def => typeof row[def.key] === "number"));
}

const METRIC_LABELS: Record<StabilityMetric, string> = {
  mdape: "MdAPE (per-impression drift)",
  wape: "WAPE (volume-weighted drift)",
};

export function stabilityMetricLabel(metric: StabilityMetric): string {
  return METRIC_LABELS[metric];
}
